import { Drawer, Box, Link } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ModalTitle } from 'src/components/new/modal/ModalTitle';
import { ModalContent } from 'src/components/new/modal/ModalContent';
import { ModalFooter } from 'src/components/new/modal/ModalFooter';
import { TextField } from 'src/components/new/inputs/TextField';
import { Typography } from 'src/components/new/Typography';
import ChangeEmail from './ChangeEmail';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { format, useMask } from '@react-input/mask';
import { countryPhoneInfo } from 'src/utils/constant';
import Select from 'src/components/new/inputs/Select';
import parsePhoneNumberFromString from 'libphonenumber-js';

export interface EditableProfile {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: keyof typeof countryPhoneInfo;
  cellPhoneNumber: string;
  directOfficePhoneNumber: string;
  directOfficePhoneNumberExtension: string;
}

interface EditProfileDataProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (profileInfo: EditableProfile) => void;
  onPasswordReset: () => void;
  onEmailChange: (data: any) => void;
  info: EditableProfile;
}

interface ProfileDataErrors {
  firstNameError: string;
  lastNameError: string;
  cellPhoneNumberError: string;
  emailError: string;
  directOfficePhoneNumberError: string;
  directOfficePhoneNumberExtensionError: string;
}

export const EditProfileData: React.FC<EditProfileDataProps> = ({
  open,
  onClose,
  onSubmit,
  info,
  onPasswordReset,
  onEmailChange,
}) => {
  const [showEmailFields, setShowEmailFields] = useState(false);
  const [newProfileInfo, setNewProfileInfo] = useState<EditableProfile>({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: 'US',
    cellPhoneNumber: '',
    directOfficePhoneNumber: '',
    directOfficePhoneNumberExtension: '',
  });

  const isPasswordResetLoading = useSelector((state: RootState) => state.user.passwordResetForm.isLoading);

  const [errors, setErrors] = useState<ProfileDataErrors>({
    firstNameError: '',
    lastNameError: '',
    emailError: '',
    cellPhoneNumberError: '',
    directOfficePhoneNumberError: '',
    directOfficePhoneNumberExtensionError: '',
  });

  const mask = countryPhoneInfo[newProfileInfo.countryCode].mask;

  const phoneRef = useMask({
    mask,
    replacement: { '_': /\d/ },
    showMask: true
  });

  const directPhoneRef = useMask({
    mask,
    replacement: { '_': /\d/ },
    showMask: true
  });

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setNewProfileInfo((prevState) => ({ ...prevState, [name]: value }));
  };

  useEffect(() => {
    if (open && info !== undefined) {
      setNewProfileInfo(info);
    }

    return () => {
      if (!open) {
        setNewProfileInfo({
          firstName: '',
          lastName: '',
          email: '',
          countryCode: 'US',
          cellPhoneNumber: '',
          directOfficePhoneNumber: '',
          directOfficePhoneNumberExtension: '',
        });
      }
    };
  }, [open]);

  const handleSubmit = () => {
    const newErrors: ProfileDataErrors = {
      firstNameError: '',
      lastNameError: '',
      emailError: '',
      cellPhoneNumberError: '',
      directOfficePhoneNumberError: '',
      directOfficePhoneNumberExtensionError: '',
    };

    if (!newProfileInfo.firstName) {
      newErrors.firstNameError = 'First Name cannot be empty!';
    }
    if (!newProfileInfo.lastName) {
      newErrors.lastNameError = 'Last Name cannot be empty!';
    }

    const cellPhone = parsePhoneNumberFromString(newProfileInfo.cellPhoneNumber, newProfileInfo.countryCode);
    const directOfficePhone = parsePhoneNumberFromString(
      newProfileInfo.directOfficePhoneNumber,
      newProfileInfo.countryCode,
    );

    if (cellPhone && !cellPhone.isValid()) {
      newErrors.cellPhoneNumberError = 'Wrong phone number format';
    }

    if (directOfficePhone && !directOfficePhone.isValid()) {
      newErrors.directOfficePhoneNumberError = 'Wrong phone number format';
    }

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== '');

    if (!hasErrors) {
      onSubmit({
        ...newProfileInfo,
        cellPhoneNumber: newProfileInfo.cellPhoneNumber ? newProfileInfo.cellPhoneNumber.replace(/\D/g, '') : '',
        directOfficePhoneNumber: newProfileInfo.directOfficePhoneNumber
          ? newProfileInfo.directOfficePhoneNumber.replace(/\D/g, '')
          : '',
      });
    }
  };

  useEffect(() => {
    let cellPhoneDigits = newProfileInfo.cellPhoneNumber.replace(/\D/g, '');
    let directPhoneDigits = newProfileInfo.directOfficePhoneNumber.replace(/\D/g, '');
    setNewProfileInfo((prevState) => ({
      ...prevState,
      cellPhoneNumber: format(cellPhoneDigits, { mask, replacement: { '_': /\d/ } }),
      directOfficePhoneNumber: format(directPhoneDigits, { mask, replacement: { '_': /\d/ } }),
    }));
  }, [newProfileInfo.countryCode]);

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={onClose}
      sx={{
        '& .MuiPaper-root': {
          background: '#FFF',
        },
      }}
    >
      <ModalTitle>Edit Profile</ModalTitle>
      <ModalContent>
        <Box style={{ marginBottom: '1rem', width: '100%' }}>
          <Box
            style={{
              width: '100%',
              display: 'flex',
              gap: '0.625rem',
            }}
          >
            <TextField
              id="firstName"
              label="First Name"
              value={newProfileInfo.firstName}
              name="firstName"
              required={true}
              error={errors.firstNameError}
              onChange={onChange}
            />
            <TextField
              id="lastName"
              required={true}
              label="Last Name"
              value={newProfileInfo.lastName}
              name="lastName"
              error={errors.lastNameError}
              onChange={onChange}
            />
          </Box>
          <TextField
            id="email"
            label="Email"
            value={newProfileInfo.email}
            name="email"
            required={true}
            error=""
            disabled={true}
            onChange={onChange}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              onClick={() => setShowEmailFields((prev) => !prev)}
              fontSize={14}
              fontWeight={600}
              sx={{ color: '#436eb2', textDecoration: 'underline', cursor: 'pointer', mb: 2, mt: -2 }}
            >
              {showEmailFields ? 'Cancel Email Changes' : 'Change Email'}
            </Typography>
            {showEmailFields && <ChangeEmail onEmailChange={onEmailChange} />}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Select
              containerSx={{ width: '50%' }}
              label="Country Code"
              required
              data={Object.entries(countryPhoneInfo).map(([id, { code }]) => ({
                id,
                label: `${id} (${code})`,
              }))}
              onChange={(e) => setNewProfileInfo((prevState) => ({ ...prevState, countryCode: e.target.value }))}
              value={newProfileInfo.countryCode}
              disabled={false}
              error=""
            />
            <TextField
              containerSX={{ width: '50%' }}
              id="cellPhoneNumber"
              inputRef={phoneRef}
              label="Cell Phone"
              placeholder={mask}
              value={newProfileInfo.cellPhoneNumber}
              name="cellPhoneNumber"
              error={errors.cellPhoneNumberError}
              onChange={onChange}
            />
          </Box>
          <Box style={{ width: '100%', display: 'flex', gap: '0.625rem' }}>
            <TextField
              id="directOfficePhoneNumber"
              label="Direct Office Phone"
              inputRef={directPhoneRef}
              value={newProfileInfo.directOfficePhoneNumber}
              name="directOfficePhoneNumber"
              placeholder={mask}
              error={errors.directOfficePhoneNumberError}
              containerSX={{ flex: 3 }}
              onChange={onChange}
            />
            <TextField
              id="directOfficePhoneNumberExtension"
              label="Ext."
              value={newProfileInfo.directOfficePhoneNumberExtension}
              name="directOfficePhoneNumberExtension"
              error={errors.directOfficePhoneNumberExtensionError}
              containerSX={{ flex: 1 }}
              onChange={onChange}
            />
          </Box>
          {isPasswordResetLoading ? (
            <Typography>Sending link to email...</Typography>
          ) : (
            <Typography
              fontSize={14}
              onClick={onPasswordReset}
              fontWeight={600}
              sx={{ color: '#436eb2', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Reset Password
            </Typography>
          )}
        </Box>
      </ModalContent>
      <ModalFooter onClose={onClose} onSubmit={handleSubmit} closeText="Cancel" submitText="Save" />
    </Drawer>
  );
};
